import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  ToggleArchiveWorkScheduleDocument,
  WorkScheduleScheduleFragment,
  WorkSchedulesContainerDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import Settings from './Settings'

export interface Props {
  archived?: boolean
  schedule: WorkScheduleScheduleFragment
  disabled: boolean
  canAssignEmployees: boolean
  hasFixedWorkSchedulesEnabled: boolean
}

const TitleBar = ({
  archived,
  schedule,
  disabled,
  canAssignEmployees,
  hasFixedWorkSchedulesEnabled,
}: Props) => {
  const { id, name, employeeIds } = schedule
  const period = schedule.overlapPeriods.filter((period) => period.default)[0]
  const isFixed = period?.scheduleType === 'fixed'
  const count = employeeIds.length

  const employees = count
    ? i18n.tp('settings.work_schedule.table_data.employee', { count })
    : i18n.t('settings.work_schedule.table_data.employee.zero')

  const fixedConfig = {
    color: Tag.Types.malibu,
    text: i18n.t('settings.work_schedule.table_data.tags.fixed'),
  }

  const flexibleConfig = {
    color: Tag.Types.purple,
    text: i18n.t('settings.work_schedule.table_data.tags.flexible'),
  }

  const tagConfig = isFixed ? fixedConfig : flexibleConfig
  const showEmployeesInfo = !archived && !disabled

  const borderConfig = {
    color: 'grey400',
    width: 's1',
    style: 'solid',
  } as const

  const [toggleArchive] = useMutation(ToggleArchiveWorkScheduleDocument, {
    extractorFn: (data) => data.workScheduleMutations.toggleArchiveWorkscheduleSchedule,
    refetchQueries: [WorkSchedulesContainerDocument],
  })

  return (
    <Box
      background="grey200"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      flexGrow
      width="full"
      paddingY="s16"
      paddingX="s24"
      borderRadius={{
        topLeft: 'abs008',
        topRight: 'abs008',
      }}
      border={{
        top: borderConfig,
        left: borderConfig,
        right: borderConfig,
      }}
    >
      <Box>
        <Text.Header2>{name}</Text.Header2>
        {showEmployeesInfo && <Text.Normal>{employees}</Text.Normal>}
      </Box>
      <Box flexDirection="row" alignItems="center" gap="s16">
        <Tag type={tagConfig.color}>{tagConfig.text}</Tag>
        {!disabled &&
          (!archived ? (
            <Settings
              id={id}
              isFixed={isFixed}
              canAssignEmployees={canAssignEmployees}
              hasFixedWorkSchedulesEnabled={hasFixedWorkSchedulesEnabled}
            />
          ) : (
            <Button
              onClick={() => toggleArchive({ variables: { id } })}
              type="secondary"
              iconName="archive-open"
            >
              {i18n.t('settings.work_schedule.table_data.unarchive')}
            </Button>
          ))}
      </Box>
    </Box>
  )
}

export default observer(TitleBar)
